import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  ReceiptText,
  Wifi,
  MessageSquareWarning,
  Eye,
  EyeOff,
  MessageCircle,
  RotateCcw,
  CheckCircle2,
  Send,
  Search,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#050914]/70 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-cyan-400/60 focus:outline-none";
const labelCls = "mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400";

const TABS = [
  { id: "tagihan", label: "Cek Tagihan", Icon: ReceiptText },
  { id: "wifi", label: "Ubah WiFi", Icon: Wifi },
  { id: "keluhan", label: "Sampaikan Keluhan", Icon: MessageSquareWarning },
];

const COMPLAINT_CATEGORIES = [
  "Internet Lambat",
  "Internet Mati Total",
  "Masalah Tagihan",
  "Lainnya",
];

function SuccessCard({ id, waUrl, onReset, title }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="service-success-panel"
      className="py-4 text-center"
    >
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
      <h3 className="mt-4 font-display text-xl font-bold text-white">{title}</h3>
      <p className="mt-2 text-sm text-slate-400">Nomor referensi:</p>
      <p data-testid="service-success-ref" className="mt-1 font-mono text-xl font-bold text-cyan-400">
        {id}
      </p>
      <p className="mx-auto mt-3 max-w-sm text-sm text-slate-400">
        Teruskan ke CS agar permintaan Anda lebih cepat diproses (maks. 1×24 jam):
      </p>
      <a
        data-testid="service-success-wa"
        href={waUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-400"
      >
        <MessageCircle className="h-4 w-4" /> Teruskan ke WhatsApp CS
      </a>
      <div>
        <button
          data-testid="service-success-reset"
          onClick={onReset}
          className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-white"
        >
          <RotateCcw className="h-4 w-4" /> Buat Permintaan Baru
        </button>
      </div>
    </motion.div>
  );
}

function BillingPanel() {
  const [ident, setIdent] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrMsg("");
    setResult(null);
    try {
      const res = await fetch(`${API}/billing/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: ident }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal memeriksa tagihan");
      setResult(data.customer);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const lunas = result.bill_status === "lunas";
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="billing-result"
        className="rounded-2xl border border-white/10 bg-[#050914]/60 p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-display text-lg font-bold text-white">{result.full_name}</p>
            <p className="mt-0.5 font-mono text-xs text-slate-400">
              {result.customer_id} • {result.package_name}
            </p>
          </div>
          <span
            data-testid="billing-status"
            className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${
              lunas ? "bg-emerald-400/15 text-emerald-300" : "bg-[#F04E23]/15 text-[#FF6B35]"
            }`}
          >
            {lunas ? "LUNAS" : "BELUM LUNAS"}
          </span>
        </div>
        <div className="mt-5 border-t border-white/10 pt-5">
          <p className="text-xs text-slate-400">Tagihan periode {result.period}</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-white">
            Rp {Number(result.bill_amount).toLocaleString("id-ID")}
          </p>
          <p className="mt-1 font-mono text-xs text-slate-400">Jatuh tempo: {result.due_date}</p>
        </div>
        {!lunas && (
          <a
            data-testid="billing-pay-wa"
            href={waLink(
              `Halo CS Fasnet Fiber, saya ${result.full_name} (${result.customer_id}) ingin membayar tagihan periode ${result.period} sebesar Rp ${Number(result.bill_amount).toLocaleString("id-ID")}`
            )}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-400"
          >
            <MessageCircle className="h-4 w-4" /> Bayar / Konfirmasi via WhatsApp
          </a>
        )}
        <button
          data-testid="billing-check-again"
          onClick={() => {
            setResult(null);
            setIdent("");
          }}
          className="mt-4 w-full text-center text-sm text-slate-400 transition-colors duration-200 hover:text-white"
        >
          Cek nomor lain
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={check} data-testid="billing-form" className="space-y-4">
      <div>
        <label className={labelCls}>ID Pelanggan / No. HP Terdaftar</label>
        <input
          data-testid="billing-input"
          required
          minLength={4}
          value={ident}
          onChange={(e) => setIdent(e.target.value)}
          placeholder="contoh: FSF-1001 atau 0812xxxxxxxx"
          className={inputCls}
        />
      </div>
      {errMsg && (
        <p
          data-testid="billing-error"
          className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300"
        >
          {errMsg}
        </p>
      )}
      <button
        data-testid="btn-check-billing"
        disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F04E23] py-3.5 font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35] disabled:opacity-50"
      >
        <Search className="h-4 w-4" /> {busy ? "Memeriksa..." : "Cek Tagihan Saya"}
      </button>
      <p className="text-center font-mono text-xs text-slate-500">
        Akun demo: FSF-1001 (belum lunas) • FSF-1002 (lunas)
      </p>
    </form>
  );
}

function WifiPanel() {
  const empty = { full_name: "", phone_number: "", customer_id: "", wifi_ssid: "", wifi_password: "" };
  const [form, setForm] = useState(empty);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.wifi_password.length < 8) return toast.error("Password WiFi minimal 8 karakter");
    setBusy(true);
    try {
      const res = await fetch(`${API}/service-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "wifi_change", ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal mengirim permintaan");
      setDone(data);
      toast.success("Permintaan ubah WiFi terkirim!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <SuccessCard
        id={done.id}
        waUrl={done.whatsapp_url}
        title="Permintaan Ubah WiFi Diterima!"
        onReset={() => {
          setDone(null);
          setForm(empty);
        }}
      />
    );

  return (
    <form onSubmit={submit} data-testid="wifi-form" className="space-y-4">
      <div>
        <label className={labelCls}>Nama Lengkap</label>
        <input data-testid="wifi-input-name" required minLength={3} value={form.full_name} onChange={set("full_name")} placeholder="Nama pemilik akun" className={inputCls} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>No. WhatsApp Terdaftar</label>
          <input data-testid="wifi-input-phone" required type="tel" minLength={8} value={form.phone_number} onChange={set("phone_number")} placeholder="08xxxxxxxxxx" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>ID Pelanggan (opsional)</label>
          <input data-testid="wifi-input-customer-id" value={form.customer_id} onChange={set("customer_id")} placeholder="contoh: FSF-1001" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Nama WiFi Baru (SSID)</label>
        <input data-testid="wifi-input-ssid" required value={form.wifi_ssid} onChange={set("wifi_ssid")} placeholder="contoh: Fasnet_BudiHome" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Password WiFi Baru</label>
        <div className="relative">
          <input
            data-testid="wifi-input-password"
            required
            minLength={8}
            type={showPw ? "text" : "password"}
            value={form.wifi_password}
            onChange={set("wifi_password")}
            placeholder="Minimal 8 karakter"
            className={`${inputCls} pr-12`}
          />
          <button
            type="button"
            data-testid="wifi-toggle-password"
            onClick={() => setShowPw(!showPw)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors duration-200 hover:text-white"
            aria-label="Tampilkan password"
          >
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <button
        data-testid="btn-submit-wifi"
        disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F04E23] py-3.5 font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35] disabled:opacity-50"
      >
        <Send className="h-4 w-4" /> {busy ? "Mengirim..." : "Kirim Permintaan Ubah WiFi"}
      </button>
      <p className="text-center text-xs text-slate-500">
        Perubahan berlaku setelah diproses tim kami (maks. 1×24 jam).
      </p>
    </form>
  );
}

function ComplaintPanel() {
  const empty = { full_name: "", phone_number: "", customer_id: "", complaint_category: "", complaint_detail: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.complaint_category) return toast.error("Pilih kategori keluhan dulu ya");
    if (form.complaint_detail.trim().length < 10) return toast.error("Detail keluhan minimal 10 karakter");
    setBusy(true);
    try {
      const res = await fetch(`${API}/service-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "complaint", ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal mengirim keluhan");
      setDone(data);
      toast.success("Keluhan berhasil dikirim!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <SuccessCard
        id={done.id}
        waUrl={done.whatsapp_url}
        title="Keluhan Anda Tercatat!"
        onReset={() => {
          setDone(null);
          setForm(empty);
        }}
      />
    );

  return (
    <form onSubmit={submit} data-testid="complaint-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Nama Lengkap</label>
          <input data-testid="complaint-input-name" required minLength={3} value={form.full_name} onChange={set("full_name")} placeholder="Nama Anda" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>No. WhatsApp</label>
          <input data-testid="complaint-input-phone" required type="tel" minLength={8} value={form.phone_number} onChange={set("phone_number")} placeholder="08xxxxxxxxxx" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Kategori Keluhan</label>
        <div data-testid="complaint-categories" className="flex flex-wrap gap-2">
          {COMPLAINT_CATEGORIES.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`complaint-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setForm({ ...form, complaint_category: c })}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                form.complaint_category === c
                  ? "border-[#F04E23] bg-[#F04E23]/15 text-white"
                  : "border-white/10 bg-[#050914]/60 text-slate-400 hover:border-white/25"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Detail Keluhan</label>
        <textarea
          data-testid="complaint-input-detail"
          required
          rows={4}
          value={form.complaint_detail}
          onChange={set("complaint_detail")}
          placeholder="contoh: internet mati sejak tadi malam, lampu LOS merah di modem..."
          className={inputCls}
        />
      </div>
      <button
        data-testid="btn-submit-complaint"
        disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F04E23] py-3.5 font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35] disabled:opacity-50"
      >
        <Send className="h-4 w-4" /> {busy ? "Mengirim..." : "Kirim Keluhan"}
      </button>
    </form>
  );
}

export default function ServicePage() {
  const [tab, setTab] = useState("tagihan");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050914] font-sans text-slate-100 antialiased">
      <Navbar />
      <main className="pt-[72px]">
        <section data-testid="service-page" className="relative mx-auto max-w-3xl px-5 py-16 sm:py-24">
          <div className="pointer-events-none absolute -top-10 left-1/2 h-[300px] w-[500px] -translate-x-1/2 rounded-full bg-[#1E5AA8]/20 blur-[140px]" />
          <p className="relative font-mono text-xs tracking-[0.25em] text-cyan-400">
            LAYANAN PELANGGAN
          </p>
          <h1 className="relative mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Cek tagihan & bantuan mandiri.
          </h1>
          <p className="relative mt-4 max-w-xl text-base text-slate-300">
            Tanpa perlu antre: periksa tagihan, minta ubah nama & password WiFi, atau sampaikan
            keluhan — semua dari halaman ini.
          </p>

          <div data-testid="service-tabs" className="relative mt-8 flex flex-wrap gap-2">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`tab-${id}`}
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  tab === id
                    ? "border-[#F04E23] bg-[#F04E23]/15 text-white"
                    : "border-white/10 bg-[#0A1124]/60 text-slate-400 hover:border-white/25"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>

          <div className="relative mt-8 rounded-3xl border border-white/10 bg-[#0A1124]/80 p-6 backdrop-blur-xl sm:p-8">
            {tab === "tagihan" && <BillingPanel />}
            {tab === "wifi" && <WifiPanel />}
            {tab === "keluhan" && <ComplaintPanel />}
          </div>
        </section>
      </main>
      <Footer />
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
